import { useState } from 'react'
import { useQuery } from 'react-query'
import { Link, useNavigate } from 'react-router-dom'
import { endpoints } from '../services/cfoApi'
import { usePeriodo } from '../context/PeriodoContext'
import { PeriodoActivo } from '../components/common/FiltroPeriodo'
import { Leyenda } from '../components/common/leyendas'
import ThOrden from '../components/common/ThOrden'
import {
  ArrowTrendingUpIcon,
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  BuildingOfficeIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  UserGroupIcon,
  ChartBarIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline'
import { exportarExcel, traerTodo } from '../services/excel'

const fmtQ = (n) => `Q${(Number(n) || 0).toLocaleString('es-GT', { maximumFractionDigits: 2 })}`
const fmtDate = (d) => {
  if (!d) return '—'
  const s = typeof d === 'string' ? d : String(d)
  return s.slice(0, 10)
}

const badgeForDias = (dias) => {
  if (dias <= 0) return { label: 'Al corriente', color: 'badge-success', icon: CheckCircleIcon }
  if (dias <= 30) return { label: `${dias}d`,      color: 'badge-warning', icon: ClockIcon }
  if (dias <= 60) return { label: `${dias}d`,      color: 'badge-warning', icon: ExclamationCircleIcon }
  return                       { label: `${dias}d`, color: 'badge-danger',  icon: ExclamationCircleIcon }
}

// Columnas del Excel
const COLUMNAS_CXC_CLIENTES = [
  { label: 'Código', get: r => r.codigo_cliente },
  { label: 'Cliente', get: r => r.cliente },
  { label: 'Sector', get: r => r.sector },
  { label: 'Documentos', get: r => r.documentos },
  { label: 'Saldo', get: r => Number(r.saldo).toFixed(2) },
  { label: '% del total', get: r => r.pct_saldo },
  { label: 'Vencido', get: r => Number(r.vencido).toFixed(2) },
  { label: 'Documentos vencidos', get: r => r.documentos_vencidos },
  { label: 'Atraso máximo (días)', get: r => r.atraso_max },
  { label: 'Días de crédito', get: r => r.dias_credito },
  { label: 'Días reales de pago', get: r => r.dias_pago },
  { label: '% pagado a tiempo', get: r => r.pct_a_tiempo },
]
const COLUMNAS_CXC_DOCS = [
  { label: 'Código cliente', get: r => r.codigo_cliente },
  { label: 'Cliente', get: r => r.cliente },
  { label: 'Forma de pago', get: r => r.forma_pago },
  { label: 'Sucursal', get: r => r.sucursal || r.codigo_sucursal },
  { label: 'Vendedor', get: r => r.vendedor },
  { label: 'Documento', get: r => `${r.tipo_documento} ${r.documento}` },
  { label: 'Emisión', get: r => r.fecha_emision && String(r.fecha_emision).slice(0, 10) },
  { label: 'Vencimiento', get: r => r.fecha_vencimiento && String(r.fecha_vencimiento).slice(0, 10) },
  { label: 'Días de atraso', get: r => r.dias_atraso },
  { label: 'Saldo', get: r => r.saldo_total.toFixed(2) },
  { label: 'Por vencer', get: r => r.porvencer.toFixed(2) },
  { label: 'Vencido', get: r => r.vencido.toFixed(2) },
  { label: '1-30', get: r => r.v30.toFixed(2) },
  { label: '31-60', get: r => r.v31a60.toFixed(2) },
  { label: '61-90', get: r => r.v61a90.toFixed(2) },
  { label: '91-120', get: r => r.v91a120.toFixed(2) },
  { label: '+120', get: r => r.v120.toFixed(2) },
  { label: 'Días crédito (ficha)', get: r => r.dias_credito_ficha },
  { label: 'Días según facturas', get: r => r.dias_segun_facturas },
  { label: 'Estado', get: r => r.estado_cxc },
]

export default function CuentasPorCobrar() {
  const [busqueda, setBusqueda] = useState('')
  const [bucket, setBucket]     = useState('todos')
  const [vista, setVista]       = useState('clientes') // 'clientes' | 'documentos'
  const navigate = useNavigate()
  const irACliente = (id) => navigate(`/tesoreria/cuentas-por-cobrar/cliente/${id}`)

  const { params: periodo } = usePeriodo()
  const { data: cxcData, isLoading: loadingResumen } = useQuery(
    ['cxc', periodo], () => endpoints.tesoreria.cxc(periodo), { keepPreviousData: true }
  )

  const [orden, setOrden] = useState({ k: 'saldo', dir: 'desc' })
  const detalleParams = { limit: 500, offset: 0, busqueda, bucket, orden: orden.k, dir: orden.dir, ...periodo }
  const { data: detalleData, isLoading: loadingDetalle, isFetching } = useQuery(
    ['cxc-detalle', busqueda, bucket, orden, periodo],
    () => endpoints.tesoreria.cxcDetalle(detalleParams),
    { keepPreviousData: true, enabled: vista === 'documentos' }
  )
  const { data: clientesData, isLoading: loadingClientes, isFetching: fetchingClientes } = useQuery(
    ['cxc-clientes', busqueda, periodo],
    () => endpoints.tesoreria.cxcClientes({ busqueda, ...periodo }),
    { keepPreviousData: true, enabled: vista === 'clientes' }
  )
  const clientes = clientesData?.data?.filas || []

  const resumen       = cxcData?.data || {}
  const distribucion  = resumen.distribucion_aging || {}
  const totalFacturas = resumen.facturas || 0

  const ord = { orden, setOrden }
  const filas = detalleData?.data?.filas || []
  const totalFilas   = detalleData?.data?.total_filas || 0
  const sumaFiltrada = detalleData?.data?.suma_saldo || 0

  // Exporta la vista activa (clientes o documentos) con la búsqueda y el filtro de antigüedad
  const [exportando, setExportando] = useState(false)
  const exportar = async () => {
    setExportando(true)
    try {
      const corte = periodo.hasta || 'hoy'
      if (vista === 'clientes') {
        const res = clientesData || await endpoints.tesoreria.cxcClientes({ busqueda, ...periodo })
        await exportarExcel(`cxc_clientes_${corte}.xlsx`, COLUMNAS_CXC_CLIENTES, res?.data?.filas || [], { hoja: 'Clientes' })
      } else {
        const docs = await traerTodo(endpoints.tesoreria.cxcDetalle, { busqueda, bucket, orden: orden.k, dir: orden.dir, ...periodo })
        await exportarExcel(`cxc_documentos_${corte}.xlsx`, COLUMNAS_CXC_DOCS, docs, { hoja: 'Documentos' })
      }
    } finally {
      setExportando(false)
    }
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/tesoreria"
            className="w-10 h-10 rounded-lg bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] flex items-center justify-center transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-[var(--text-muted)]" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#001639] flex items-center justify-center">
              <ArrowTrendingUpIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">Cuentas por Cobrar</h1>
              <p className="text-sm text-[var(--text-muted)]">
                {loadingResumen ? 'Cargando…' : <>{totalFacturas.toLocaleString()} documentos abiertos • atraso promedio {resumen.promedio_dias_cobro || 0} días <Leyenda k="atraso_promedio" /></>}
              </p>
              <PeriodoActivo nota="saldos de documentos emitidos en el período" className="mt-1" />
            </div>
          </div>
        </div>
        <button onClick={exportar} disabled={exportando} className="btn-secondary flex items-center gap-2 disabled:opacity-60">
          <ArrowDownTrayIcon className="w-4 h-4" />
          {exportando ? 'Exportando…' : 'Exportar Excel'}
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="kpi-card card-hover">
          <span className="kpi-label flex items-center gap-1">Total por Cobrar <Leyenda k="cxc_total" /></span>
          <p className="kpi-value">{fmtQ(resumen.total_cxc)}</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">{totalFacturas.toLocaleString()} documentos</p>
        </div>
        <div className="kpi-card card-hover">
          <span className="kpi-label flex items-center gap-1">Al Corriente (por vencer) <Leyenda k="por_vencer" /></span>
          <p className="kpi-value text-[var(--success)]">{fmtQ(distribucion.al_corriente?.monto)}</p>
          <p className="text-xs text-[var(--success)] mt-1">{distribucion.al_corriente?.porcentaje || 0}%</p>
        </div>
        <div className="kpi-card card-hover">
          <span className="kpi-label flex items-center gap-1">1-30 días vencido <Leyenda k="dias_vencido" /></span>
          <p className="kpi-value text-[var(--warning)]">{fmtQ(distribucion._30_dias?.monto)}</p>
          <p className="text-xs text-[var(--warning)] mt-1">{distribucion._30_dias?.porcentaje || 0}%</p>
        </div>
        <div className="kpi-card card-hover">
          <span className="kpi-label flex items-center gap-1">+60 días (riesgo) <Leyenda k="cxc_riesgo" /></span>
          <p className="kpi-value text-[var(--danger)]">{fmtQ((distribucion._60_dias?.monto || 0) + (distribucion._90_dias?.monto || 0))}</p>
          <p className="text-xs text-[var(--danger)] mt-1">Atención requerida</p>
        </div>
      </div>

      {/* Aging */}
      <div className="card">
        <div className="section-header">
          <ChartBarIcon className="w-5 h-5 text-[var(--text-muted)]" />
          <h2 className="font-semibold">Distribución por Antigüedad</h2>
          <Leyenda k="aging_cxc" />
        </div>
        <div className="p-5 pt-0 space-y-4">
          {[
            { key: 'al_corriente', label: 'Por vencer',            color: 'bg-emerald-500' },
            { key: '_30_dias',     label: '1-30 días vencido',     color: 'bg-amber-500' },
            { key: '_60_dias',     label: '31-60 días vencido',    color: 'bg-orange-500' },
            { key: '_90_dias',     label: '60+ días vencido',      color: 'bg-rose-500' }
          ].map(r => {
            const val = distribucion[r.key]
            return (
              <div key={r.key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--text-secondary)]">{r.label}</span>
                  <span className="text-sm font-semibold tabular-nums">
                    {fmtQ(val?.monto)} ({val?.porcentaje || 0}%)
                  </span>
                </div>
                <div className="h-3 bg-[var(--bg-tertiary)] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${r.color} transition-all duration-700`}
                    style={{ width: `${val?.porcentaje || 0}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex rounded-lg border border-[var(--border-default)] overflow-hidden shrink-0 self-start sm:self-auto">
          {[{ id: 'clientes', label: 'Por cliente' }, { id: 'documentos', label: 'Por documento' }].map(v => (
            <button
              key={v.id}
              onClick={() => setVista(v.id)}
              className={`px-4 py-2 text-sm font-medium ${vista === v.id ? 'bg-[#001639] text-white' : 'hover:bg-[var(--bg-secondary)] text-[var(--text-secondary)]'}`}
            >{v.label}</button>
          ))}
        </div>
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="w-5 h-5 text-[var(--text-muted)] absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Buscar por cliente, código o número de documento…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="input w-full pl-12"
          />
        </div>
        {vista === 'documentos' && <select
          value={bucket}
          onChange={(e) => setBucket(e.target.value)}
          className="input min-w-[200px]"
        >
          <option value="todos">Todos los buckets</option>
          <option value="al_corriente">Por vencer</option>
          <option value="_30_dias">1-30 días vencido</option>
          <option value="_60_dias">31-60 días vencido</option>
          <option value="_90_dias">60+ días vencido</option>
        </select>}
      </div>

      {vista === 'clientes' && (
        <TablaClientes
          filas={clientes}
          cargando={loadingClientes}
          actualizando={fetchingClientes && !loadingClientes}
          total={clientesData?.data?.suma_saldo || 0}
          onClick={irACliente}
        />
      )}

      {/* Tabla */}
      {vista === 'documentos' && <div className="card overflow-hidden">
        <div className="p-4 border-b border-[var(--border-default)] flex items-center justify-between bg-[var(--bg-secondary)]">
          <div className="flex items-center gap-2">
            <UserGroupIcon className="w-5 h-5 text-[var(--text-muted)]" />
            <span className="text-sm text-[var(--text-muted)]">
              {loadingDetalle ? 'Cargando…' : `${filas.length.toLocaleString()} de ${totalFilas.toLocaleString()} documentos`}
              {isFetching && !loadingDetalle && ' • actualizando…'}
            </span>
          </div>
          <span className="text-sm font-semibold">Total filtrado: {fmtQ(sumaFiltrada)}</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[var(--bg-secondary)] border-b border-[var(--border-default)]">
              <tr>
                <ThOrden k="cliente" texto {...ord}>Cliente</ThOrden>
                <ThOrden k="sucursal" texto {...ord}>Sucursal</ThOrden>
                <ThOrden k="vendedor" texto {...ord}>Vendedor</ThOrden>
                <ThOrden k="documento" {...ord}>Documento</ThOrden>
                <ThOrden k="emision" {...ord}>Emisión</ThOrden>
                <ThOrden k="vencimiento" {...ord}>Vencimiento</ThOrden>
                <ThOrden k="credito" align="center" {...ord}>Crédito <Leyenda k="credito_ficha_factura" /><br /><span className="normal-case font-normal">ficha · factura</span></ThOrden>
                <ThOrden k="saldo" align="right" {...ord}>Saldo</ThOrden>
                <ThOrden k="dias" align="center" {...ord}>Días <Leyenda k="dias_vencido" /></ThOrden>
                <ThOrden k="estado" texto align="center" {...ord}>Estado</ThOrden>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-default)]">
              {loadingDetalle && filas.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-sm text-[var(--text-muted)]">
                    Cargando cartera desde el ERP…
                  </td>
                </tr>
              )}
              {!loadingDetalle && filas.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-sm text-[var(--text-muted)]">
                    No hay documentos que coincidan con el filtro.
                  </td>
                </tr>
              )}
              {filas.map(row => {
                const badge = badgeForDias(row.dias_atraso)
                const BadgeIcon = badge.icon
                return (
                  <tr key={row.id} className="hover:bg-[var(--bg-secondary)] transition-colors cursor-pointer" onClick={() => irACliente(row.cliente_id)} title="Ver ficha del cliente">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[var(--bg-tertiary)] flex items-center justify-center">
                          <BuildingOfficeIcon className="w-4 h-4 text-[var(--text-muted)]" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-[var(--text-primary)] truncate max-w-[240px] hover:underline">{row.cliente}</p>
                          <p className="text-xs text-[var(--text-muted)]">
                            {row.codigo_cliente}{row.forma_pago ? ` · ${row.forma_pago}` : ''}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-[var(--text-secondary)]">{row.sucursal || row.codigo_sucursal}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-[var(--text-secondary)]">{row.vendedor || '—'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium">{row.tipo_documento} {row.documento}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-[var(--text-secondary)] tabular-nums">{fmtDate(row.fecha_emision)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-[var(--text-secondary)] tabular-nums">{fmtDate(row.fecha_vencimiento)}</p>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {row.dias_credito_ficha !== null && row.dias_segun_facturas !== null ? (
                        <>
                          <p className="text-sm tabular-nums">
                            <span className="text-[var(--text-muted)]">{row.dias_credito_ficha}d</span>
                            <span className="mx-1 text-[var(--text-muted)]">·</span>
                            <span className={`font-semibold ${
                              (row.dias_segun_facturas - row.dias_credito_ficha) > 5 ? 'text-[var(--warning)]' :
                              (row.dias_segun_facturas - row.dias_credito_ficha) < -5 ? 'text-[var(--success)]' : ''
                            }`}>{row.dias_segun_facturas}d</span>
                          </p>
                          {Math.abs(row.dias_segun_facturas - row.dias_credito_ficha) > 5 && (
                            <p className={`text-xs tabular-nums ${
                              (row.dias_segun_facturas - row.dias_credito_ficha) > 0 ? 'text-[var(--warning)]' : 'text-[var(--success)]'
                            }`}>
                              {(row.dias_segun_facturas - row.dias_credito_ficha) > 0 ? '+' : ''}
                              {row.dias_segun_facturas - row.dias_credito_ficha}d
                            </p>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-[var(--text-muted)]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-bold tabular-nums">{fmtQ(row.saldo_total)}</span>
                      {row.vencido > 0 && (
                        <p className="text-xs text-[var(--danger)] tabular-nums">Vencido {fmtQ(row.vencido)}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-sm font-semibold ${
                        row.dias_atraso > 60 ? 'text-[var(--danger)]' :
                        row.dias_atraso > 30 ? 'text-[var(--warning)]' : 'text-[var(--text-secondary)]'
                      }`}>
                        {row.dias_atraso}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1.5 ${badge.color}`}>
                        <BadgeIcon className="w-3.5 h-3.5" />
                        {row.estado_cxc || badge.label}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>}
    </div>
  )
}

// Cartera agrupada por cliente; cada fila abre la ficha del cliente
function TablaClientes({ filas, cargando, actualizando, total, onClick }) {
  const [orden, setOrden] = useState({ k: 'saldo', dir: -1 })
  const ordenadas = [...filas].sort((a, b) => {
    const x = a[orden.k] ?? -Infinity, y = b[orden.k] ?? -Infinity
    return (typeof x === 'string' ? x.localeCompare(y) : x - y) * orden.dir
  })
  const Th = ({ k, children, align = 'right', leyenda }) => (
    <th
      onClick={() => setOrden(o => ({ k, dir: o.k === k ? -o.dir : -1 }))}
      className={`px-4 py-3 text-${align} text-xs font-semibold text-[var(--text-muted)] uppercase cursor-pointer select-none whitespace-nowrap hover:text-[var(--text-secondary)]`}
    >
      {children}{orden.k === k ? (orden.dir < 0 ? ' ↓' : ' ↑') : ''} {leyenda && <Leyenda k={leyenda} />}
    </th>
  )
  return (
    <div className="card overflow-hidden">
      <div className="p-4 border-b border-[var(--border-default)] flex items-center justify-between gap-2 flex-wrap bg-[var(--bg-secondary)]">
        <div className="flex items-center gap-2">
          <UserGroupIcon className="w-5 h-5 text-[var(--text-muted)]" />
          <span className="text-sm text-[var(--text-muted)]">
            {cargando ? 'Cargando…' : `${filas.length.toLocaleString()} clientes con saldo · clic para ver su ficha`}
            {actualizando && ' • actualizando…'}
          </span>
          <Leyenda k="cxc_por_cliente" />
        </div>
        <span className="text-sm font-semibold">Total: {fmtQ(total)}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[var(--bg-secondary)] border-b border-[var(--border-default)]">
            <tr>
              <Th k="cliente" align="left">Cliente</Th>
              <Th k="saldo">Saldo</Th>
              <Th k="por_vencer" leyenda="por_vencer">Por vencer</Th>
              <Th k="vencido" leyenda="dias_vencido">Vencido</Th>
              <Th k="atraso_max">Atraso máx.</Th>
              <Th k="dias_credito" leyenda="dias_credito">Crédito</Th>
              <Th k="dias_pago" leyenda="dias_pago_real">Paga en</Th>
              <Th k="pct_a_tiempo" leyenda="pct_a_tiempo">A tiempo</Th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border-default)]">
            {cargando && filas.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-[var(--text-muted)]">Cargando cartera…</td></tr>
            )}
            {!cargando && filas.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-[var(--text-muted)]">No hay clientes que coincidan.</td></tr>
            )}
            {ordenadas.map(r => {
              const lento = r.dias_pago !== null && r.dias_credito !== null && r.dias_pago > r.dias_credito + 5
              return (
                <tr key={r.cliente_id} onClick={() => onClick(r.cliente_id)} className="hover:bg-[var(--bg-secondary)] transition-colors cursor-pointer group">
                  <td className="px-4 py-3">
                    <p className="font-medium text-[var(--text-primary)] truncate max-w-[280px] group-hover:underline">{r.cliente}</p>
                    <p className="text-xs text-[var(--text-muted)]">{r.codigo_cliente}{r.sector ? ` · ${r.sector}` : ''} · {r.documentos} doc{r.documentos === 1 ? '' : 's'}</p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <p className="font-bold tabular-nums">{fmtQ(r.saldo)}</p>
                    <p className="text-xs text-[var(--text-muted)] tabular-nums">{r.pct_saldo}% del total</p>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-sm text-[var(--text-secondary)]">{fmtQ(r.por_vencer)}</td>
                  <td className={`px-4 py-3 text-right tabular-nums text-sm ${r.vencido > 0 ? 'text-[var(--danger)] font-semibold' : 'text-[var(--text-muted)]'}`}>
                    {r.vencido > 0 ? fmtQ(r.vencido) : '—'}
                    {r.documentos_vencidos > 0 && <p className="text-xs font-normal">{r.documentos_vencidos} doc{r.documentos_vencidos === 1 ? '' : 's'}</p>}
                  </td>
                  <td className={`px-4 py-3 text-right tabular-nums text-sm ${r.atraso_max > 60 ? 'text-[var(--danger)] font-semibold' : r.atraso_max > 0 ? 'text-[var(--warning)]' : 'text-[var(--text-muted)]'}`}>
                    {r.atraso_max > 0 ? `${r.atraso_max} d` : '—'}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-sm text-[var(--text-secondary)]">{r.dias_credito !== null ? `${r.dias_credito} d` : '—'}</td>
                  <td className={`px-4 py-3 text-right tabular-nums text-sm ${lento ? 'text-[var(--warning)] font-semibold' : 'text-[var(--text-secondary)]'}`}>
                    {r.dias_pago !== null ? `${r.dias_pago} d` : '—'}
                  </td>
                  <td className={`px-4 py-3 text-right tabular-nums text-sm ${r.pct_a_tiempo === null ? 'text-[var(--text-muted)]' : r.pct_a_tiempo >= 80 ? 'text-[var(--success)]' : r.pct_a_tiempo >= 50 ? 'text-[var(--warning)]' : 'text-[var(--danger)]'}`}>
                    {r.pct_a_tiempo !== null ? `${r.pct_a_tiempo}%` : '—'}
                  </td>
                  <td className="pr-3"><ChevronRightIcon className="w-4 h-4 text-[var(--text-muted)] group-hover:text-[var(--text-primary)]" /></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
